// The candidate's screens: the shell, and the steps before the assessment.
//
// A candidate is not a Dosen user in any way that matters to them. They have no
// nav, no dashboard, no programs and no way into the rest of the product
// (C-9, and the backend enforces it with an endpoint allow-list), so these
// screens are a single column on a plain page, with the organisation's name on
// it rather than ours.
//
// The order of the steps is a legal order, not a design one:
//   RESIDENCE first, because where they live decides which notice they must see
//   (C-1b, PRD 8.1b), and because a location we cannot serve has to stop
//   everything before anything is collected or sent to Elliott.
//   NOTICE second, before onboarding and before the CV upload (C-2, L-1).
//   Then the fields, then the welcome, then the assessment.

import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LuCheck } from 'react-icons/lu';
import countryCodeArray from '../../constants/countrycode';
import dosenLogo from '../../assets/images/brand/dosen/white-logo.svg';
import { fadeInSx } from '../../components/ui/DashboardSkeletons';
import {
  ActionButton,
  BODY,
  BRANDY,
  BRANDY_TINT,
  DANGER,
  DONE,
  Field,
  FONT,
  INPUT_SX,
  LABEL,
  LINE,
  LINE_2,
  SelectField,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXT_4,
  WELL
} from '../new-designs/tabs/capability-setup/designSystem';
import { CopyLines, US_STATES } from '../new-designs/tabs/hiring/hiringParts';
import type { CandidateMeView } from '../../api/hiring';

/**
 * The steps before the assessment, named for the candidate rather than for
 * us. Shown as a rail so they can see how short this is: not knowing how much
 * is left is the cheapest reason there is to lose someone half way.
 */
const STEPS: Array<{ key: CandidateMeView['step']; label: string }> = [
  { key: 'residence', label: 'Where you live' },
  { key: 'notice', label: 'How it works' },
  { key: 'onboarding', label: 'About you' },
  { key: 'welcome', label: 'Your assessment' }
];

const StepRail: React.FC<{ step: CandidateMeView['step'] }> = ({ step }) => {
  const here = STEPS.findIndex((item) => item.key === step);
  if (here < 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      {STEPS.map((item, index) => {
        const done = index < here;
        const current = index === here;
        return (
          <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box
                sx={{
                  width: current ? 18 : 6,
                  height: 6,
                  borderRadius: '3px',
                  bgcolor: done || current ? BRANDY : LINE_2,
                  opacity: done ? 0.55 : 1,
                  transition: 'all .25s ease'
                }}
              />
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: 11.5,
                  fontWeight: current ? 600 : 500,
                  color: current ? TEXT : done ? TEXT_3 : TEXT_4,
                  whiteSpace: 'nowrap'
                }}
              >
                {item.label}
              </Typography>
            </Box>
            {index < STEPS.length - 1 && (
              <Box sx={{ width: 14, height: 1, bgcolor: LINE, display: { xs: 'none', sm: 'block' } }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * The page every candidate screen sits in. The org's name leads, not Dosen's.
 *
 * This is the first thing a candidate sees of the company hiring them, so it
 * is built to be looked at: one column on a dark page, a brandy glow behind
 * the card to lift it off the flat black, and the actions always in view.
 *
 * `footer` pins the actions to the bottom of the card and lets the BODY
 * scroll instead of the page. The notice is long because the law makes it
 * long, and when the page scrolled, the tick box and the button sat below the
 * window with nothing on screen to say they were there.
 */
export const CandidateShell: React.FC<{
  me: CandidateMeView;
  title: string;
  subtitle?: string;
  footer?: React.ReactNode;
  /** More small print after the line about Elliott, for a screen that has more to say. */
  note?: string;
  children: React.ReactNode;
}> = ({ me, title, subtitle, footer, note, children }) => (
  <Box
    sx={{
      height: '100vh',
      overflow: 'hidden',
      bgcolor: '#0F0F0F',
      display: 'flex',
      flexDirection: 'column',
      py: { xs: '24px', sm: '40px' },
      px: '16px',
      position: 'relative',
      // The app has no global border-box reset (index.css sets it on `body`
      // alone, and quotes the value, so the browser drops it). These screens
      // live outside the product's Layout, where MUI's own components hide
      // that.
      boxSizing: 'border-box',
      '& *, & *::before, & *::after': { boxSizing: 'border-box' },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -220,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 760,
        height: 460,
        background: `radial-gradient(ellipse at center, ${BRANDY_TINT} 0%, rgba(247,148,32,0) 70%)`,
        pointerEvents: 'none'
      }
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 680,
        mx: 'auto',
        my: 'auto',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        ...fadeInSx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...LABEL, color: BRANDY }}>{me.orgName}</Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: { xs: 22, sm: 26 },
              fontWeight: 600,
              lineHeight: 1.2,
              color: TEXT,
              mt: '6px'
            }}
          >
            {title}
          </Typography>
          {subtitle && <Typography sx={{ ...BODY, fontSize: 13.5, mt: '8px' }}>{subtitle}</Typography>}
        </Box>
        <Box component="img" src={dosenLogo} alt="Dosen" sx={{ height: 18, opacity: 0.45, flexShrink: 0, mt: '2px' }} />
      </Box>

      <StepRail step={me.step} />

      <Box
        sx={{
          flex: footer ? 1 : 'none',
          minHeight: 0,
          bgcolor: SURFACE,
          border: `1px solid ${LINE}`,
          borderRadius: '12px',
          boxShadow: '0 18px 48px rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            flex: footer ? 1 : 'none',
            minHeight: 0,
            overflowY: footer ? 'auto' : 'visible',
            p: { xs: '20px', sm: '26px' },
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          {children}
        </Box>
        {footer && (
          <Box
            sx={{
              borderTop: `1px solid ${LINE}`,
              bgcolor: WELL,
              p: { xs: '16px 20px', sm: '18px 26px' },
              display: 'flex',
              flexDirection: 'column',
              gap: '14px'
            }}
          >
            {footer}
          </Box>
        )}
      </Box>

      {/* L-2: Elliott is identified as an AI wherever the candidate meets it. */}
      <Typography sx={{ ...BODY, fontSize: 11.5, color: TEXT_4, textAlign: 'center', lineHeight: 1.6 }}>
        This assessment is run with Elliott, an AI assistant, to help {me.orgName} understand more about the candidates.
        A person at {me.orgName} reviews every result and makes all hiring decisions. No decision is made by Elliott.
        {note ? ` ${note}` : ''}
      </Typography>
    </Box>
  </Box>
);

/** C-1b: asked before the notice, because it decides which notice applies. */
export const ResidenceStep: React.FC<{
  me: CandidateMeView;
  busy: boolean;
  error: string | null;
  onSubmit: (input: { country: string; state?: string }) => void;
}> = ({ me, busy, error, onSubmit }) => {
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const countries = useMemo(
    () => [...countryCodeArray].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ id: c.code, name: c.name })),
    []
  );
  const needsState = country === 'US';
  const ready = Boolean(country) && (!needsState || Boolean(state));
  return (
    <CandidateShell me={me} title={`${me.roleName} Assessment`}>
      {/* The product's own select, not the browser's: a native dropdown on a
          dark page renders in the operating system's colours and looks like
          a different application. */}
      <Field
        label="Which country do you live in?"
        required
        help="Your country decides which information and rights notices apply to you."
      >
        <SelectField
          label="Which country do you live in?"
          options={countries}
          value={country}
          placeholder="Choose a country"
          onChange={(id) => {
            setCountry(id);
            setState('');
          }}
        />
      </Field>
      {needsState && (
        <Field label="Which state?" required>
          <SelectField
            label="Which state?"
            options={US_STATES}
            value={state}
            placeholder="Choose a state"
            onChange={setState}
          />
        </Field>
      )}
      {error && (
        <Typography sx={{ ...BODY, color: DANGER }} role="alert">
          {error}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ActionButton
          primary
          disabled={!ready || busy}
          reason={!ready ? 'Choose where you live' : undefined}
          onClick={() => onSubmit({ country, state: needsState ? state : '' })}
        >
          {busy ? 'Saving...' : 'Continue'}
        </ActionButton>
      </Box>
    </CandidateShell>
  );
};

/**
 * C-2, L-1: the notice. It cannot be skipped and the box is never pre-ticked,
 * and we record which version they saw and when, because being able to prove we
 * gave notice is the obligation.
 */
export const NoticeStep: React.FC<{
  me: CandidateMeView;
  busy: boolean;
  error: string | null;
  onAcknowledge: (version: string) => void;
}> = ({ me, busy, error, onAcknowledge }) => {
  const [ticked, setTicked] = useState(false);
  if (!me.notice) return null;
  return (
    <CandidateShell
      me={me}
      title={me.notice.title}
      // The notice is long because the law makes it long. It scrolls inside
      // the card, so the tick box and the button never leave the screen.
      footer={
        <NoticeActions
          me={me}
          busy={busy}
          error={error}
          ticked={ticked}
          onTick={setTicked}
          onAcknowledge={onAcknowledge}
        />
      }
    >
      <CopyLines block={me.notice} />
    </CandidateShell>
  );
};

/** The part of the notice that must stay in view: the tick and the button. */
const NoticeActions: React.FC<{
  me: CandidateMeView;
  busy: boolean;
  error: string | null;
  ticked: boolean;
  onTick: (ticked: boolean) => void;
  onAcknowledge: (version: string) => void;
}> = ({ me, busy, error, ticked, onTick, onAcknowledge }) => (
  <>
    <Box
      component="label"
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        cursor: 'pointer',
        p: '12px',
        borderRadius: '4px',
        bgcolor: WELL,
        border: `1px solid ${LINE}`
      }}
    >
      <Box
        component="input"
        type="checkbox"
        checked={ticked}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTick(e.target.checked)}
        sx={{ mt: '2px', accentColor: BRANDY, width: 15, height: 15, flexShrink: 0 }}
      />
      <Typography sx={{ ...BODY, color: TEXT_2 }}>
        I have read and understood how this assessment works and how my data will be used.
      </Typography>
    </Box>
    {error && (
      <Typography sx={{ ...BODY, color: DANGER }} role="alert">
        {error}
      </Typography>
    )}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <ActionButton
        primary
        disabled={!ticked || busy}
        reason={!ticked ? 'Please confirm you have read it' : undefined}
        onClick={() => onAcknowledge(me.notice!.version)}
      >
        {busy ? 'Saving...' : 'I understand, continue'}
      </ActionButton>
    </Box>
  </>
);

/** C-3, H-6: the onboarding fields the owner chose, the CV and LinkedIn. */
export const OnboardingStep: React.FC<{
  me: CandidateMeView;
  busy: boolean;
  error: string | null;
  onSave: (input: { answers: Record<string, string>; linkedinUrl: string }) => void;
  onUploadCv: (file: File) => void;
}> = ({ me, busy, error, onSave, onUploadCv }) => {
  const [answers, setAnswers] = useState<Record<string, string>>(me.answers);
  const [linkedinUrl, setLinkedinUrl] = useState(me.linkedinUrl);
  const cvInput = useRef<HTMLInputElement>(null);
  // Only what the owner chose to ask for: anything they left unselected isn't asked at all.
  const asked = me.fields.filter((f) => f.required);
  const textFields = asked.filter((f) => f.key !== 'cv' && f.key !== 'linkedin');
  const cvField = asked.find((f) => f.key === 'cv');
  const linkedinField = asked.find((f) => f.key === 'linkedin');
  const missing =
    textFields.some((f) => f.required && !(answers[f.key] || '').trim()) ||
    (cvField?.required && !me.hasCv) ||
    (linkedinField?.required && !linkedinUrl.trim());

  return (
    <CandidateShell me={me} title="A few details">
      {textFields.map((field) => (
        <Field key={field.key} label={field.label || field.key} required={field.required}>
          <Box
            component="input"
            value={answers[field.key] || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAnswers((current) => ({ ...current, [field.key]: e.target.value }))
            }
            sx={INPUT_SX}
          />
        </Field>
      ))}

      {cvField && (
        <Field
          label="Your CV"
          required={cvField.required}
          help="PDF or Word, up to 10 MB. Elliott reads the text of it against the capabilities for this role, and ignores anything personal such as your age, nationality or where you studied."
        >
          {me.hasCv ? (
            <Typography sx={{ ...BODY, color: TEXT }}>
              Uploaded: {me.cvFileName || 'your CV'}. Choose another file to replace it.
            </Typography>
          ) : null}
          {/* The browser's own input always says "No file chosen", so it stays hidden behind a button. */}
          <Box
            ref={cvInput}
            component="input"
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) onUploadCv(file);
              // So choosing the same file again after a failed upload still fires.
              e.target.value = '';
            }}
            sx={{ display: 'none' }}
          />
          <Box sx={{ alignSelf: 'flex-start' }}>
            <ActionButton disabled={busy} onClick={() => cvInput.current?.click()}>
              Choose File
            </ActionButton>
          </Box>
        </Field>
      )}

      {linkedinField && (
        <Field label="LinkedIn" required={linkedinField.required}>
          <Box
            component="input"
            value={linkedinUrl}
            placeholder="https://www.linkedin.com/in/..."
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLinkedinUrl(e.target.value)}
            sx={INPUT_SX}
          />
        </Field>
      )}

      {error && (
        <Typography sx={{ ...BODY, color: DANGER }} role="alert">
          {error}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ActionButton
          primary
          disabled={Boolean(missing) || busy}
          reason={missing ? 'Please fill in everything marked required' : undefined}
          onClick={() => onSave({ answers, linkedinUrl })}
        >
          {busy ? 'Saving...' : 'Continue'}
        </ActionButton>
      </Box>
    </CandidateShell>
  );
};

/** C-4: the owner's welcome message, the auto Notes, and Begin Assessment. */
export const WelcomeStep: React.FC<{
  me: CandidateMeView;
  busy: boolean;
  error: string | null;
  onBegin: () => void;
}> = ({ me, busy, error, onBegin }) => (
  <CandidateShell me={me} title={`${me.roleName} Assessment`}>
    {me.welcomeMessage && (
      <Typography sx={{ ...BODY, color: TEXT, whiteSpace: 'pre-wrap' }}>{me.welcomeMessage}</Typography>
    )}
    {me.notes && (
      <Box
        sx={{
          p: '16px',
          borderRadius: '4px',
          bgcolor: WELL,
          border: `1px solid ${LINE}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}
      >
        <Typography sx={{ ...LABEL, color: TEXT_2 }}>{me.notes.title}</Typography>
        <CopyLines block={me.notes} small bullets />
      </Box>
    )}
    {error && (
      <Typography sx={{ ...BODY, color: DANGER }} role="alert">
        {error}
      </Typography>
    )}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <ActionButton primary disabled={busy} onClick={onBegin}>
        {busy ? 'Starting...' : 'Begin assessment'}
      </ActionButton>
    </Box>
  </CandidateShell>
);

/**
 * C-8: the completion screen. No levels and no analysis: those go to the
 * reviewer, and the candidate never sees a result.
 *
 * What it CAN do is close properly. This is the last thing a candidate sees
 * of the company hiring them, and a wall of grey text is a poor goodbye: it
 * names what they finished, says what happens next in order, and keeps their
 * rights where they can still read them.
 */
export const DoneStep: React.FC<{ me: CandidateMeView }> = ({ me }) => (
  <CandidateShell
    me={me}
    title={`Thank you, ${me.firstName || 'and well done'}`}
    note={`Your data is kept for ${
      me.retention
    } after this hiring round closes, then deleted. You can ask for an explanation of how AI was used, for a person to review the result, to contest it, or for your data to be corrected or deleted${
      me.contactEmail ? `, at ${me.contactEmail}` : ' at the contact address in your invitation'
    }.`}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,151,45,0.12)',
          border: `1px solid ${DONE}`,
          color: DONE
        }}
      >
        <LuCheck size={22} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: TEXT }}>
          Your {me.roleName} assessment is complete
        </Typography>
        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3, mt: '2px' }}>
          {me.items.length} {me.items.length === 1 ? 'capability' : 'capabilities'}, finished in full
        </Typography>
      </Box>
    </Box>

    <Box sx={{ height: '1px', bgcolor: 'rgba(247, 148, 32, 0.45)' }} />

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Typography sx={{ ...LABEL, color: TEXT_3 }}>What happens now</Typography>
      <WhatNext n={1} title={`A person at ${me.orgName} reviews your assessment`}>
        Elliott&apos;s levels are advice to them. Every hiring decision is theirs.
      </WhatNext>
      <WhatNext n={2} title={`${me.orgName} gets in touch`}>
        They will contact you about the outcome
        {me.contactEmail ? `, and you can reach them at ${me.contactEmail}` : ''}.
      </WhatNext>
    </Box>
  </CandidateShell>
);

/** One numbered step of what happens after the assessment. */
const WhatNext: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <Box sx={{ display: 'flex', gap: '12px' }}>
    <Box
      sx={{
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        border: `1px solid ${LINE_2}`,
        color: BRANDY,
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: 700
      }}
    >
      {n}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: BRANDY }}>{title}</Typography>
      <Typography sx={{ ...BODY, fontSize: 12.5, mt: '2px' }}>{children}</Typography>
    </Box>
  </Box>
);

/** C-1b: the block screen. Nothing beyond their name, email and country was collected. */
export const BlockedStep: React.FC<{ me: CandidateMeView }> = ({ me }) => (
  <CandidateShell me={me} title="We can't run this assessment where you are">
    <Typography sx={{ ...BODY, fontSize: 14, color: TEXT }}>
      {me.blockedMessage || `This assessment isn't available in your location yet. Please contact ${me.orgName}.`}
    </Typography>
    <Typography sx={{ ...BODY }}>
      We have told {me.orgName} so they can get in touch with you directly. Nothing beyond your name, email address and
      country has been collected, and nothing has been sent to the AI.
    </Typography>
    {me.contactEmail && <Typography sx={{ ...BODY }}>You can reach them at {me.contactEmail}.</Typography>}
  </CandidateShell>
);

/** H-27: the round closed before they finished, or their invite was revoked. */
export const ClosedStep: React.FC<{ me: CandidateMeView }> = ({ me }) => (
  <CandidateShell me={me} title="This assessment has closed">
    <Typography sx={{ ...BODY, fontSize: 14, color: TEXT }}>
      This assessment is no longer open. If you think that is a mistake, please contact {me.contactEmail || me.orgName}.
    </Typography>
  </CandidateShell>
);
